// Pantry screen — the entry point for the MCM cooking-app experiment.
// Warm parchment backdrop, glass search + filter bar, 2-column grid
// of glass item cards. Each card shows: emoji icon, name (serif),
// quantity (mono), location pill, teal status dot.
//
// Accepts either a real pantry array (from App.jsx `usePantry`) or
// falls back to the hardcoded DEMO_ITEMS when rendered standalone
// (Showcase.jsx). `items` is detected by shape — real pantry rows
// carry `.amount` / `.unit` / `.expiresAt`; demo rows pre-baked
// `.qty` / `.days` / `.status`. The adapter below normalizes both
// into the card's render shape so the visual layer doesn't care
// which source it got.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import {
  GlassPanel,
  StatusDot, Kicker, SerifHeader, FadeIn,
  BottomDock,
  withAlpha,
} from "./primitives";
import { useTheme, THEME_TRANSITION } from "./theme";
import { font } from "./tokens";
import { matchPantryRow } from "../../lib/matchPantryRow";
import { findIngredient } from "../../data/ingredients";

// Tile system — re-using the exact same classifier the classic
// Kitchen tile view uses, so an item that lives under "Dairy & Eggs"
// in the old UI lives in the SAME tile here. Location axis (fridge
// / pantry / freezer) sits above the tile axis per CLAUDE.md.
import {
  LOCATIONS, NAV_TABS,
  toCard, makeClassifierHelpers,
  sumLocationTiles, firstExpiring, sortItems, isRecent,
} from "./helpers";
import { useIngredientInfo } from "../../lib/useIngredientInfo";
import { ReceiptButton, CartButton, HeroToolbarCluster } from "./HeroToolbar";
import { FloatingLocationDock } from "./FloatingLocationDock";
import BulkSelectBar from "./BulkSelectBar";
import { useToast } from "../../lib/toast";
import { isPinned, togglePinned } from "../../lib/aiRecipePinned";
import { TileGrid } from "./TileGrid";
import { ItemGrid } from "./ItemGrid";
import { HubDrillGrid } from "./HubDrillGrid";
import { StackDetailSheet } from "./StackDetailSheet";
import { DrilledTileHeader } from "./DrilledTileHeader";
import { TriageCTA, FreshCTA, ShowcaseDemoCTA } from "./BottomCTA";
import { TileGridSkeleton } from "./TileGridSkeleton";
import { EmptyState, LocationEmptyState } from "./EmptyStates";
import { SearchSummary, SearchGlyph } from "./SearchSummary";
import { useNow, formatClock } from "./useNow";

// Hardcoded demo items — only used when KitchenScreen is rendered
// standalone (Showcase.jsx) without an `items` prop. Kept so the
// design-reference surface keeps working untouched.
const DEMO_ITEMS = [
  { id: 1, emoji: "🧈", name: "Kerrygold Butter",    qty: "1 stick",    location: "Dairy & Eggs",    cat: "dairy",   status: "ok",      days: 12 },
  { id: 2, emoji: "🥚", name: "Pasture Eggs",         qty: "8 large",    location: "Dairy & Eggs",    cat: "dairy",   status: "ok",      days: 18 },
  { id: 3, emoji: "🥛", name: "Whole Milk",           qty: "½ gallon",   location: "Dairy & Eggs",    cat: "dairy",   status: "warn",    days: 3 },
  { id: 4, emoji: "🧀", name: "Gruyère",              qty: "6 oz",       location: "Dairy & Eggs",    cat: "dairy",   status: "ok",      days: 22 },
  { id: 5, emoji: "🍋", name: "Meyer Lemons",         qty: "4 whole",    location: "Produce",         cat: "produce", status: "ok",      days: 9 },
  { id: 6, emoji: "🧄", name: "Garlic",               qty: "1 head",     location: "Produce",         cat: "produce", status: "ok",      days: 30 },
  { id: 7, emoji: "🥬", name: "Tuscan Kale",          qty: "1 bunch",    location: "Produce",         cat: "produce", status: "warn",    days: 2 },
  { id: 8, emoji: "🍅", name: "San Marzano",          qty: "28 oz can",  location: "Pantry",          cat: "pantry",  status: "ok",      days: 180 },
  { id: 9, emoji: "🍞", name: "Sourdough Loaf",       qty: "1 loaf",     location: "Pantry",          cat: "pantry",  status: "ok",      days: 5 },
  { id:10, emoji: "🫒", name: "Olive Oil",            qty: "500 ml",     location: "Pantry",          cat: "pantry",  status: "ok",      days: 120 },
  { id:11, emoji: "🐟", name: "Wild Salmon",          qty: "0.75 lb",    location: "Meat & Seafood",  cat: "meat",    status: "warn",    days: 1 },
  { id:12, emoji: "🍗", name: "Chicken Thighs",       qty: "1.5 lb",     location: "Meat & Seafood",  cat: "meat",    status: "ok",      days: 3 },
];

export default function KitchenScreen({
  items,
  // When true, the pantry is still fetching from Supabase on cold
  // mount. Shows a skeleton state instead of the empty-shelf copy
  // so the user doesn't see "Empty shelf. Time for a grocery run."
  // flash on a pantry they know has items — just a brief ghost grid
  // while the initial query resolves.
  loading = false,
  onOpenItem,
  onStartCooking,
  onOpenUnitPicker,
  // Shopping-cart bridge. When provided, renders a cart button in
  // the hero's top-right corner that calls back to App.jsx to
  // switch pantryView → "shopping" (which falls through to the
  // classic Kitchen shopping list view). shoppingCount drives an
  // optional count badge on the cart. Both undefined = no button
  // (keeps Showcase clean).
  onGoToShopping,
  shoppingCount = 0,
// Receipts bridge — onOpenReceipts callback + current-month
  // spend in cents. When wired, the hero top-right shows a
  // spend chip + receipt button alongside the cart. Both
  // undefined in Showcase.
  onOpenReceipts,
  spendCents = 0,
  // Swipe-to-remove handler — called with the raw pantry row
  // when the user swipes a card open and confirms the Remove
  // action. App.jsx wires this to setPantry filter; Showcase
  // leaves it undefined (no remove action shown).
  onRemoveItem,
  // Inline-update handler — called from KitchenCard when the
  // user adjusts a row in place (currently the tappable fill
  // gauge; future inline editors land here too). Receives
  // (rawRow, partialPatch) and the parent merges. Showcase
  // leaves undefined so the gauge stays read-only there.
  onUpdateItem,
  // Manual / scan add — called when the "+" button is tapped.
  // App.jsx wires this to mount the MCMAddDraftSheet overlay.
  // Showcase leaves undefined.
  onOpenAdd,
  // Hold-menu callbacks for KitchenCard's flip action panel.
  // onDeleteItem(raw) removes the row from pantry (App.jsx wires to
  // setPantry-filter); onRestockItem(raw) appends it to the shopping
  // list. Both optional — Showcase leaves them undefined and the
  // back-face buttons disable themselves accordingly.
  onDeleteItem,
  onRestockItem,
  // Bulk cleanup (select mode). onDeleteItems(rows[]) removes many
  // rows in ONE setPantry filter (one sync diff, one render);
  // onRestoreItems(rows[]) re-adds them — powers the undo toast.
  // Both optional; the SELECT affordance hides without them.
  onDeleteItems = null,
  onRestoreItems = null,
  // Bulk Restock — (rows[]) → shopping list in one deduped write.
  onRestockItems = null,
  hideDock = false,
}) {
  const { theme } = useTheme();
  // Live ingredient_info for AI-routed `tile_routing` overrides —
  // threaded into the classifier helpers via toCard(...) below so
  // canonicals re-categorized by `scripts/ai_recategorize_misc.mjs`
  // land on their assigned tile instead of misc.
  const { getInfo } = useIngredientInfo();
  const classifierHelpers = useMemo(
    () => makeClassifierHelpers(getInfo),
    [getInfo]
  );
  // Three-level navigation mirroring the classic Kitchen:
  //   locationTab  — fridge / pantry / freezer
  //   drilledTile  — which tile inside that location is expanded
  //                  (null = show the tile grid, set = show items)
  //   query        — when typed, bypasses the hierarchy and shows
  //                  matching items globally (cross-location) so a
  //                  search for "butter" surfaces both the fridge
  //                  stick AND the frozen cultured butter block
  //                  without the user having to guess which tab.
  const [locationTab, setLocationTab] = useState("fridge");
  const [drilledTile, setDrilledTile] = useState(null);
  // Sub-state for the tile drill-down. When a tile contains items
  // that share a hub canonical (chicken_hub, beef_hub, cheese_hub,
  // milk_hub, …) the drilled view shows a hub picker first; tapping
  // a hub sets `drilledHub` and the items grid filters to that hub's
  // children. Mirrors ShopModePlacementWizard step 2's hub→leaf
  // pattern. Null = at the top of the tile drill (hub picker visible).
  const [drilledHub, setDrilledHub] = useState(null);
  const [query, setQuery] = useState("");
  // Sort order for the drilled-tile items grid. Defaults to
  // "expiring" which surfaces whichever warn items are in this
  // tile at the top — the most useful default for a triage-
  // oriented pantry. Not applied to search results (those stay
  // in the pantry row's native order so the user sees relevance,
  // not an arbitrary sort).
  const [sortBy, setSortBy] = useState("expiring");
  // Category filter inside a drilled tile. null = no filter
  // (all items in the tile shown). String value = a typeLabel
  // (e.g. "Cheese") that matches each card's `typeLabel`.
  // Resets when the user changes drilled tile, switches
  // location, or starts searching.
  const [categoryFilter, setCategoryFilter] = useState(null);
  // Active overall-kitchen filter — null (default tile grid), "good"
  // (only ok-status items, flat list), "soon" (only expiring-soon
  // items), or "recent" (created within the last 14 days). Mirrors
  // the search-mode pattern: when set, the body renders a flat list
  // of matching cards instead of the tile grid.
  const [activeFilter, setActiveFilter] = useState(null);
  const RECENT_WINDOW_MS = 14 * 24 * 60 * 60 * 1000; // 14 days
  // Single source of truth for which item card has its swipe-
  // to-remove drawer open. iOS pattern — only one card can be
  // open at a time. When user swipes another card, the previous
  // one auto-closes via the prop cascade in KitchenCard. null
  // means none open.
  // Tracks whether the search input has keyboard focus so the
  // surrounding GlassPanel can show a focus ring. The panel's
  // own border transitions to a warm teal accent when focused —
  // subtler than a browser default outline, stronger than
  // nothing.
  const [searchFocused, setSearchFocused] = useState(false);
  // Scroll-scrim state. True when the user has scrolled past
  // the hero enough that the sticky nav bar needs a drop shadow
  // to visually lift off the scroll body below — otherwise it
  // feels welded to the content, or worse, looks like a render
  // bug when the content scrolls "through" the bar without any
  // separation cue.
  const [scrolled, setScrolled] = useState(false);
  // Stack-detail sheet — opens when the user taps a "×N" badge on a
  // grouped KitchenCard (same canonical + brand + size + claims +
  // tile). Holds the member rows for that group; clearing closes
  // the sheet. See src/lib/kitchenGrouping.js for the bucket shape
  // and StackDetailSheet.jsx for the drill-in.
  const [openStackMembers, setOpenStackMembers] = useState(null);
  // Sentinel ref — an empty div right above the sticky bar.
  // IntersectionObserver watches it: when the sentinel is out
  // of view, the user has scrolled past the hero and the bar
  // should raise its shadow; when it's back in view, the bar
  // resets to flat. IO is cheap and never runs handlers on
  // animation frames, so this is scroll-lockstep but doesn't
  // block the main thread.
  const sentinelRef = useRef(null);
  // Ref to the search input so the "/" keyboard shortcut can
  // focus it without the user having to click the field.
  const searchInputRef = useRef(null);

  // Global keyboard shortcuts for the pantry screen:
  //   "/"   — focus the search input (the same shortcut Gmail,
  //           GitHub, Slack, and every search-first surface
  //           uses; users who try it find it working without
  //           a hint).
  //   Esc   — pop out one level of nav: close search first,
  //           otherwise back out of a drilled tile.
  // Ignored when the user is already typing in an input /
  // textarea / contenteditable, so the "/" key in a field
  // types a slash like it should.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { rootMargin: "-1px 0px 0px 0px", threshold: 0 }
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, []);

  // Scroll-to-top on drill IN / OUT / search toggle is hooked into
  // AnimatePresence's onExitComplete (search for `resetScrollAfterExit`)
  // so the snap is hidden inside the fade-out → fade-in gap. The new
  // content's first paint already lands at scrollY=0; no visible
  // smooth-scroll motion across the transition.
  const resetScrollAfterExit = useCallback(() => {
    if (typeof window === "undefined") return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const isTyping = tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if (e.key === "/" && !isTyping) {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }
      if (e.key === "Escape") {
        if (query) { setQuery(""); searchInputRef.current?.blur(); return; }
        if (drilledHub) { setDrilledHub(null); return; }
        if (drilledTile) { setDrilledTile(null); return; }
      }
      // Number shortcuts 1/2/3 — jump between the three locations
      // without lifting hands off the keyboard. Maps to the same
      // LOCATIONS order rendered in the floating dock. Ignored
      // while typing so digits still enter a search field. Also
      // resets drilledTile so the user lands back at the tile
      // grid of the picked location (same behavior as tapping a
      // dock segment).
      if (!isTyping && (e.key === "1" || e.key === "2" || e.key === "3")) {
        const idx = Number(e.key) - 1;
        const loc = LOCATIONS[idx];
        if (loc) {
          e.preventDefault();
          switchLocation(loc.id);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [query, drilledTile, drilledHub]);

  // Normalize once — everything downstream reads from `cards`.
  const cards = useMemo(
    () => (items ?? DEMO_ITEMS).map(r => toCard(r, classifierHelpers)).filter(Boolean),
    [items, classifierHelpers]
  );

  // Bucket cards by (location, tileId). Rebuilds only when the
  // pantry itself changes — not on every tab flip — so tapping
  // between Fridge / Pantry / Freezer is an instant re-read.
  const cardsByLocTile = useMemo(() => {
    const map = {};
    for (const card of cards) {
      const locKey = card._location || "pantry";
      const tileKey = card._tileId || "misc";
      if (!map[locKey]) map[locKey] = {};
      if (!map[locKey][tileKey]) map[locKey][tileKey] = [];
      map[locKey][tileKey].push(card);
    }
    return map;
  }, [cards]);

  // Active location manifest — tile array + classifier + label.
  const activeLocation = useMemo(
    () => LOCATIONS.find(l => l.id === locationTab) || LOCATIONS[0],
    [locationTab]
  );

  // Search bypasses the tile hierarchy — show matching items
  // from anywhere so the user doesn't have to remember which
  // tile their grocery-run brand ended up in. Matches against
  // name, brand, food-type label, and broad category label so a
  // user typing "kerrygold", "cheese", or "dairy" all surface
  // useful hits — not just exact name substrings.
  // Multi-token matcher (src/lib/matchPantryRow.js) — same shared
  // helper Kitchen.jsx uses. Builds a search-text bag from every
  // row field carrying identity signal (name, brand, claims,
  // flavors, canonical name, ingredient tags) and requires every
  // query token to appear in the bag. "sour cream chips" hits a
  // Lay's row whose claims contain "Sour Cream & Onion" because
  // the tokens "sour", "cream", "chips" all live somewhere in the
  // row's bag — even though no single field has the full phrase.
  const searchHits = useMemo(() => {
    if (!query) return null;
    return cards.filter(it => matchPantryRow(it, query));
  }, [cards, query]);

  const goodCount = cards.filter((i) => i.status === "ok").length;
  // Cards added in the last RECENT_WINDOW_MS — driven off the raw
  // pantry row's createdAt (Date object set in usePantry.fromDb) since
  // `purchasedAt` is null for manual adds and we want both paths to
  // count as "recently added." Bulk imports (trip commit) all share
  // the same createdAt and surface together.
  const recentCount = useMemo(() => {
    const cutoff = Date.now() - RECENT_WINDOW_MS;
    return cards.filter((c) => {
      const ts = c._raw?.createdAt ? c._raw.createdAt.getTime() : 0;
      return ts >= cutoff;
    }).length;
  }, [cards]);

  // Cards matching the current `activeFilter`, scoped to the
  // location tab so toggling FRIDGE / PANTRY / FREEZER while a
  // filter is active surfaces only items in that location. Empty
  // when no filter is active. Recent-filter sorts by created_at
  // descending (newest first) so the user sees the latest add at
  // the top; good/soon keep the natural pantry ordering.
  const filterHits = useMemo(() => {
    if (!activeFilter) return null;
    const inLocation = cards.filter((c) => c._location === locationTab);
    if (activeFilter === "good") {
      return inLocation.filter((c) => c.status === "ok");
    }
    if (activeFilter === "soon") {
      return inLocation.filter((c) => c.status === "warn");
    }
    if (activeFilter === "recent") {
      const cutoff = Date.now() - RECENT_WINDOW_MS;
      return inLocation
        .filter((c) => {
          const ts = c._raw?.createdAt ? c._raw.createdAt.getTime() : 0;
          return ts >= cutoff;
        })
        .sort((a, b) => {
          const at = a._raw?.createdAt ? a._raw.createdAt.getTime() : 0;
          const bt = b._raw?.createdAt ? b._raw.createdAt.getTime() : 0;
          return bt - at;
        });
    }
    return null;
  }, [activeFilter, cards, locationTab]);

  // Cards in the active drilled tile — base set everything else in
  // the drill-down view derives from. Pulled out so hub bucketing
  // and `visible` share the same memo input.
  const drilledTileCards = useMemo(() => {
    if (!drilledTile) return [];
    return cardsByLocTile[locationTab]?.[drilledTile.id] || [];
  }, [drilledTile, cardsByLocTile, locationTab]);

  // Hub bucketing for the drilled tile — pantry items whose
  // canonical has a `parentId` (chicken_hub, beef_hub, cheese_hub,
  // milk_hub, …) cluster under that hub. Items without a hub
  // parent fall into `tileOrphans`. Mirrors the wizard's step-2
  // grouping (ShopModePlacementWizard.jsx ~lines 117-149).
  const { tileHubs, tileOrphans } = useMemo(() => {
    if (!drilledTile) return { tileHubs: [], tileOrphans: [] };
    const byHub = new Map();
    const orphansList = [];
    for (const card of drilledTileCards) {
      const ing = card.canonicalId ? findIngredient(card.canonicalId) : null;
      const parentId = ing?.parentId;
      if (parentId) {
        if (!byHub.has(parentId)) byHub.set(parentId, []);
        byHub.get(parentId).push(card);
      } else {
        orphansList.push(card);
      }
    }
    const hubsArr = [];
    for (const [parentId, children] of byHub) {
      // findIngredient, not INGREDIENTS.find — the parent can be a
      // user-minted synthetic canonical that only exists in the DB
      // layer (dbCanonicals); the bundled-array scan can't see those
      // and the hub header would fall back to a de-slugged id.
      const hubObj = findIngredient(parentId);
      hubsArr.push({
        id: parentId,
        name: hubObj?.name || parentId.replace(/_hub$/, "").replace(/_/g, " "),
        emoji: hubObj?.emoji || "•",
        childCount: children.length,
        warnCount: children.filter(c => c.status === "warn").length,
        children,
      });
    }
    // Most-stocked first, then alphabetical — same intent as the
    // tile sort (populated tiles surface first).
    hubsArr.sort((a, b) => {
      if (b.childCount !== a.childCount) return b.childCount - a.childCount;
      return String(a.name).localeCompare(String(b.name));
    });
    return { tileHubs: hubsArr, tileOrphans: orphansList };
  }, [drilledTile, drilledTileCards]);

  // Show the hub view only when grouping actually buys the user
  // something. A tile with one chicken row + nothing else doesn't
  // need a "Chicken (1 kind) →" chip in front of the grid — that's
  // pure friction. Threshold: at least one hub AND ≥3 cards in the
  // tile total, OR ≥2 distinct hubs (so a chicken+beef tile groups
  // even at low volume).
  const useHubView = useMemo(() => {
    if (!drilledTile) return false;
    if (tileHubs.length === 0) return false;
    if (tileHubs.length >= 2) return true;
    return drilledTileCards.length >= 3;
  }, [drilledTile, tileHubs, drilledTileCards]);

  const activeHub = useMemo(
    () => (drilledHub ? tileHubs.find(h => h.id === drilledHub) || null : null),
    [drilledHub, tileHubs]
  );

  // Items visible in the current tile drill-down — derived lazily
  // so the grid can reuse the same KitchenCard renderer that the
  // flat version used. When no tile drilled, `visible` is unused
  // (tile grid renders instead). Drilled items run through the
  // sort selector; search hits keep the pantry's native order so
  // a user searching "butter" sees results in whatever order they
  // were added (proxy for relevance — recent scans near the top).
  const visible = useMemo(() => {
    if (searchHits) return searchHits;
    if (filterHits) return filterHits;
    if (!drilledTile) return [];
    // When drilled into a hub, scope the items to that hub's children.
    // Otherwise the full tile (filtered by category if set).
    let items = activeHub ? activeHub.children : drilledTileCards;
    // Optional category filter — when set, keep only items whose
    // typeLabel matches. Null/missing typeLabel never matches a
    // filter, so unspecified items drop out cleanly.
    if (categoryFilter) {
      items = items.filter(it => it.typeLabel === categoryFilter);
    }
    return sortItems(items, sortBy);
  }, [searchHits, filterHits, drilledTile, drilledTileCards, activeHub, sortBy, categoryFilter]);

  // ── Bulk select mode ───────────────────────────────────────────
  // Kitchen cleanup is a batch chore ("the whole meat drawer is
  // stale"); per-card hold-menu deletes made it a grind. SELECT →
  // tap cards / ALL → REMOVE n (two-tap confirm) → undo toast.
  // Selection tracks PHYSICAL row ids (stack taps select every
  // member); protected keepsake rows are never selectable.
  const toast = useToast();
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());   // id → raw row (for delete + undo)
  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
    selectedRowsRef.current = new Map();
  }, []);
  // Any navigation out of the current view drops the selection — a
  // half-built selection must never apply to a different drawer.
  useEffect(() => { exitSelectMode(); },
    [locationTab, drilledTile, drilledHub, query, activeFilter, exitSelectMode]);
  const selectableRows = useMemo(
    () => (visible || [])
      .map((it) => it._raw || it)
      .filter((r) => r?.id && !r.protected),
    [visible],
  );
  const toggleSelectGroup = useCallback((raws) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allIn = raws.every((r) => next.has(r.id));
      for (const r of raws) {
        if (allIn) { next.delete(r.id); selectedRowsRef.current.delete(r.id); }
        else { next.add(r.id); selectedRowsRef.current.set(r.id, r); }
      }
      return next;
    });
  }, []);
  const selectAllVisible = useCallback(() => {
    const next = new Set();
    for (const r of selectableRows) {
      next.add(r.id);
      selectedRowsRef.current.set(r.id, r);
    }
    setSelectedIds(next);
  }, [selectableRows]);
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    selectedRowsRef.current = new Map();
  }, []);
  const removeSelected = useCallback(() => {
    const rows = [...selectedIds]
      .map((id) => selectedRowsRef.current.get(id))
      .filter(Boolean);
    if (rows.length === 0 || !onDeleteItems) return;
    onDeleteItems(rows);
    exitSelectMode();
    toast.push(
      `Removed ${rows.length} item${rows.length === 1 ? "" : "s"} from your kitchen`,
      {
        kind: "info",
        emoji: "",
        ...(onRestoreItems ? { undo: () => onRestoreItems(rows) } : {}),
      },
    );
  }, [selectedIds, onDeleteItems, onRestoreItems, exitSelectMode, toast]);
  // Hold-to-select — long-press on any card enters select mode with
  // that card's group pre-selected (replaces the old hold-to-flip;
  // the flip panel's actions live on the select bar now, double-tap
  // still flips for single-item work).
  const enterSelectWith = useCallback((raws) => {
    setSelectMode(true);
    toggleSelectGroup(raws);
  }, [toggleSelectGroup]);
  // Bulk Restock — selected rows → shopping list (one write, deduped
  // in App's handler). Items stay in the kitchen; this is "we're low",
  // not "it's gone".
  const restockSelected = useCallback(() => {
    const rows = [...selectedIds]
      .map((id) => selectedRowsRef.current.get(id))
      .filter(Boolean);
    if (rows.length === 0 || !onRestockItems) return;
    onRestockItems(rows);
    exitSelectMode();
    toast.push(`Added ${rows.length} item${rows.length === 1 ? "" : "s"} to the shopping list`, {
      kind: "info", emoji: "",
    });
  }, [selectedIds, onRestockItems, exitSelectMode, toast]);
  // Bulk To-Recipe — pin every selected canonical for the next AI
  // recipe draft (same pin store the card flip used). Rows without a
  // linked canonical can't pin; say so instead of silently no-oping.
  const pinSelected = useCallback(() => {
    const rows = [...selectedIds]
      .map((id) => selectedRowsRef.current.get(id))
      .filter(Boolean);
    const canonicalIds = [...new Set(
      rows.map((r) => r.canonicalId || r.ingredientId).filter(Boolean),
    )];
    if (canonicalIds.length === 0) {
      toast.push("None of these are linked to an ingredient yet — nothing to pin", {
        kind: "warn", emoji: "",
      });
      return;
    }
    for (const cid of canonicalIds) {
      if (!isPinned(null, cid)) togglePinned(null, cid);
    }
    exitSelectMode();
    toast.push(`Pinned ${canonicalIds.length} ingredient${canonicalIds.length === 1 ? "" : "s"} for your next AI recipe`, {
      kind: "info", emoji: "",
    });
  }, [selectedIds, exitSelectMode, toast]);
  const selectProps = {
    selectMode,
    selectedIds,
    onToggleSelect: toggleSelectGroup,
    onHoldSelect: onDeleteItems ? enterSelectWith : null,
  };

  // Categories present in the current drilled tile, with counts.
  // Used to render the CategoryFilter pill row above the items
  // grid. Recomputed when the drilled tile or its items change.
  const categoryOptions = useMemo(() => {
    if (!drilledTile) return [];
    // Scope to the active hub when drilled in, so the category
    // filter row reflects what's actually visible. Falls back to
    // the full tile when no hub is selected.
    const items = activeHub ? activeHub.children : drilledTileCards;
    const counts = new Map();
    for (const it of items) {
      if (!it.typeLabel) continue;
      counts.set(it.typeLabel, (counts.get(it.typeLabel) || 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ label, count }));
  }, [drilledTile, drilledTileCards, activeHub]);

  // Switching location while drilled = bail to the tile grid of
  // the new location. Same behavior as classic Kitchen.
  const switchLocation = (id) => {
    setLocationTab(id);
    setDrilledTile(null);
    setDrilledHub(null);
    setCategoryFilter(null);
  };

  // Reset the category filter whenever the drilled tile changes
  // (different tile = different categories) or when search
  // activates (search bypasses the per-tile filter axis). Also
  // reset the hub drill so a new tile starts at its hub picker.
  useEffect(() => {
    setCategoryFilter(null);
    setDrilledHub(null);
  }, [drilledTile, query]);

  // Per-tile warn count — how many items in each tile are
  // expiring soon. Renders as a small burnt-orange dot on the
  // tile card so the user can triage at a glance without drilling
  // in. Keyed by `${location}:${tileId}` so two locations with a
  // same-id tile (fridge "misc" vs pantry "misc") don't collide.
  const warnCountByTile = useMemo(() => {
    const map = {};
    for (const loc of Object.keys(cardsByLocTile)) {
      for (const tileId of Object.keys(cardsByLocTile[loc])) {
        const n = cardsByLocTile[loc][tileId].filter(c => c.status === "warn").length;
        if (n > 0) map[`${loc}:${tileId}`] = n;
      }
    }
    return map;
  }, [cardsByLocTile]);

  // Distinct brands across the whole pantry — surfaced as
  // suggestions when the user taps "+ ADD BRAND" on a card whose
  // brand is unset. Computed once over `cards` so a 200-row
  // pantry doesn't re-walk on every keystroke inside the picker.
  const brandSuggestions = useMemo(() => {
    const set = new Set();
    for (const c of cards) {
      if (c.brand) set.add(c.brand);
    }
    return [...set];
  }, [cards]);

  // Per-tile recently-added count — how many items landed in this
  // tile in the last 24h (isRecent threshold). Surfaces a small
  // teal "+N new" hint on the tile card so a fresh grocery run is
  // visible at the browse layer without having to drill in. Same
  // key shape as warnCountByTile.
  const newCountByTile = useMemo(() => {
    const map = {};
    for (const loc of Object.keys(cardsByLocTile)) {
      for (const tileId of Object.keys(cardsByLocTile[loc])) {
        const n = cardsByLocTile[loc][tileId].filter(isRecent).length;
        if (n > 0) map[`${loc}:${tileId}`] = n;
      }
    }
    return map;
  }, [cardsByLocTile]);

  // Clock ticks live in <ClockKicker /> below — isolating useNow
  // there means the once-a-minute setState only re-renders that
  // leaf, not the whole KitchenScreen and its card grid.
  const warnCount = cards.length - goodCount;

  return (
    <div style={{
      position: "relative",
      minHeight: "100dvh",
      // NOTE: no `overflow: hidden` here even though it was the
      // original default. An overflow-clip ancestor kills
      // position:sticky inside — and we want the search + location
      // tabs to stick at the top when the user scrolls through a
      // long tile. WarmBackdrop has its own `overflow: hidden` on
      // its absolute-positioned shell, so backdrop blobs are still
      // contained; this just lets the content stack breathe.
    }}>
      {/* Global focus ring for keyboard navigation. One CSS rule
          covers every custom-styled button that opts in via
          `className="mcm-focusable"` (SortSelector pills, the
          drilled back button, location tabs). `:focus-visible`
          means it only appears for keyboard focus, not mouse
          clicks — so sighted users never see an outline they
          don't need, but keyboard users always know where they
          are. `currentColor` inherits the button's text color so
          the ring tints correctly across theme variants without
          a second token lookup. */}
      <style>{`
        .mcm-focusable {
          outline: none;
          /* Subtle press feedback — every tappable surface that
             opts into mcm-focusable inherits a faint scale on
             :active so taps feel registered. CSS :active fires
             on pointerdown so the user gets immediate feedback,
             releases on pointerup so the visual lands the moment
             the action does. Kept conservative (0.97, ~120ms)
             after a previous heavier press effect read as tacky
             on the cream/glass MCM surface — this is the
             "lighter is more" version the user asked for. */
          transition: transform 120ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        .mcm-focusable:active {
          transform: scale(0.97);
        }
        @media (prefers-reduced-motion: reduce) {
          .mcm-focusable { transition: none; }
          .mcm-focusable:active { transform: none; }
        }
        .mcm-focusable:focus-visible {
          outline: 2px solid currentColor;
          outline-offset: 2px;
          border-radius: inherit;
        }
        @keyframes mcm-skeleton-pulse {
          0%, 100% { opacity: 0.35; }
          50%      { opacity: 0.65; }
        }
        @keyframes mcm-warn-aura-pulse {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1; }
        }
        .mcm-warn-aura {
          animation: mcm-warn-aura-pulse 3.6s ease-in-out infinite;
        }
        @keyframes mcm-warn-pill-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.06); }
        }
        .mcm-warn-pill {
          animation: mcm-warn-pill-pulse 2.4s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .mcm-warn-aura, .mcm-warn-pill { animation: none; }
        }
        /* On very narrow phones (<420px) hide the tile blurb and
           tighten the card's vertical gap. */
        @media (max-width: 420px) {
          .mcm-tile-blurb { display: none !important; }
          .mcm-kbd-hint { display: none !important; }
        }
      `}</style>
      {/* WarmBackdrop is mounted once at the App root (App.jsx:623)
          and once in the Showcase wrapper for standalone mode. Mounting
          a second one here stacked two backdrops, producing the visible
          "two moons drifting out of sync" duplicate effect. */}

      <div style={{
        // Content column. On phones (≤640 CSS px) this is the full
        // viewport minus 40px of gutter — the original mobile
        // prototype look. On desktop the column widens up to 960px
        // so the hero + grid don't feel "phone-zoomed-in" in a
        // laptop browser (the original 480px cap read as 2 huge
        // cards on a 1200px window).
        position: "relative",
        maxWidth: "min(960px, 100%)",
        margin: "0 auto",
        // Top padding adds env(safe-area-inset-top) so the hero
        // kicker clears the iOS notch / Dynamic Island when the
        // page renders edge-to-edge under viewport-fit=cover.
        // Bottom padding leaves runway so the scroll content
        // never ends up UNDER the FloatingLocationDock (dock sits
        // at bottom: 96, ~44px tall → reserves ~140px at the
        // bottom; the app-level nav below the dock adds ~80px
        // more). 180px total covers both without leaving a huge
        // dead gap when the dock is hidden (search mode).
        padding: "calc(28px + env(safe-area-inset-top)) 20px 180px",
      }}
      >
        {/* Top-right toolbar cluster — Receipt + Cart, portaled to
            document.body. They live OUTSIDE this content tree (and
            outside App.jsx's tab-transition motion.div) so they
            don't pay the page's load-in / opacity-fade / backdrop-
            filter compositing cost. They're hit-testable from frame
            one. See HeroToolbarCluster for the full rationale. */}
        <HeroToolbarCluster
          onOpenReceipts={onOpenReceipts}
          onGoToShopping={onGoToShopping}
          spendCents={spendCents}
          shoppingCount={shoppingCount}
        />
        {/* --- Hero — text sits DIRECTLY on the backdrop (no glass
             surface behind it), so it uses theme.color.skyInk /
             skyInkMuted instead of the regular ink. Those tokens
             flip bright on dark-sky themes (dawn/dusk/night) so
             the hero stays legible at every time of day.

             Entrance is choreographed: meta row → header →
             flourish → subtitle, staggered 70ms apart. Reads as
             the pantry "setting up" rather than popping in all at
             once — a small moment that makes first-paint feel
             intentional. */}
        <FadeIn>
          <ClockKicker tone={theme.color.skyInkMuted} />
        </FadeIn>

        <FadeIn delay={0.07}>
          <SerifHeader
            size={52}
            style={{
              // Fluid hero — shrinks on narrow viewports (clamped at
              // 36px so it stays expressive on phones) and caps at
              // 52px on desktop. `clamp` overrides the `size` prop's
              // fontSize since style spreads after the size rule
              // inside SerifHeader.
              //
              // Pale Martini display face — custom mid-century
              // hand-drawn typeface self-hosted via @font-face.
              // Single-weight static font (no variable axes) so
              // we use a plain fontWeight rather than fontVariationSettings.
              // SerifHeader's inherited italic + 300 weight gets
              // overridden since the hero wants Pale Martini's
              // own character at full strength.
              marginTop: 4,
              color: theme.color.skyInk,
              fontFamily: font.display,
              fontStyle: "normal",
              fontWeight: 500,
              letterSpacing: "0.035em",
              fontSize: "clamp(40px, 7vw, 64px)",
              lineHeight: 1.0,
            }}
          >
            The Kitchen
          </SerifHeader>
        </FadeIn>

        {/* Conditional hero subtitle. Only renders during loading
            ("Unpacking the shelves…") or on a genuinely empty
            pantry ("Nothing on the shelves yet…"). In the normal
            case (pantry has items, finished loading) the hero
            stays clean — meta chip + title + search — and the
            tile grid carries the information below. */}
        {loading && cards.length === 0 && (
          <FadeIn delay={0.14}>
            <p style={{
              marginTop: 10, fontFamily: font.serif, fontStyle: "italic",
              fontSize: 15, color: theme.color.skyInkMuted, lineHeight: 1.45,
            }}>
              Unpacking the shelves…
            </p>
          </FadeIn>
        )}
        {!loading && cards.length === 0 && (
          <FadeIn delay={0.14}>
            <p style={{
              marginTop: 10, fontFamily: font.serif, fontStyle: "italic",
              fontSize: 15, color: theme.color.skyInkMuted, lineHeight: 1.45,
              maxWidth: 360,
            }}>
              Nothing on the shelves yet.
            </p>
          </FadeIn>
        )}

        {/* Sentinel for the sticky bar's scroll-state detector.
            Sits right above the sticky bar; the IO in the effect
            above flips `scrolled` based on whether this element
            is in view. */}
        <div ref={sentinelRef} style={{ height: 1 }} aria-hidden />

        {/* LayoutGroup wraps the ENTIRE sticky+body region so
            shared layoutIds work across the sticky drilled
            header and the tile cards inside the scrolling body.
            Without this, the tile icon → drilled icon morph
            breaks when the drilled header is stickied — framer
            would scope layoutId matching to the inner body's
            AnimatePresence and miss the header that's now
            siblings-above rather than inline. */}
        <LayoutGroup>

        {/* --- Sticky search + location tabs --------------------------
            Wrapped in a position:sticky shell so the nav stays put
            when the user scrolls through a long tile. Pulls a
            negative margin on the horizontal axis so the blur
            extends edge-to-edge of the content column rather than
            clipping to the padding gutter. zIndex 5 keeps it above
            the tile/item grids but below app-level modals (the
            shared ItemCard overlay at App.jsx renders at a higher
            z). Top offset 0 — sticks flush to the viewport top.
            Backdrop-blur lets the underneath content peek through
            the bar as it scrolls past, signaling "more above."
            Drop shadow fades in once `scrolled` flips so the bar
            visibly lifts off the content below; resets when user
            scrolls back up and the sentinel comes into view. */}
        <div style={{
          position: "sticky",
          // Pin to the absolute viewport top and absorb the safe-area
          // zone with INTERNAL padding. One continuous backdrop-
          // filter surface — covers from y=0 (under the Dynamic
          // Island) through the input pill — so there's no boundary
          // for a sub-pixel seam to show through. Two-element
          // approaches (fixed cap + sticky bar) always produced a
          // visible seam because each backdrop-filter is its own
          // stacking context sampling content independently; one
          // element makes the question vanish.
          //
          // Tradeoff: in non-scrolled state the bar's bg extends env
          // pixels above the input pill (the paddingTop space), so
          // the gap between the hero and the input pill reads
          // slightly larger than before. Acceptable visual quirk —
          // any user actually scrolling the kitchen sees this padding
          // become the cap zone and the bar reads correctly. env()
          // returns 0px on non-notch devices, so desktop / older
          // iPhones see no change at all.
          //
          // CookBanner publishes its current height on
          // `--cook-banner-h` (see CookBanner.jsx). Reading it here
          // pushes the sticky pin point DOWN by that amount when a
          // cook is active — without it, the banner buries the
          // search pill at scroll. Falls back to 0 when no banner is
          // mounted, matching the original behavior.
          top: "var(--cook-banner-h, 0px)",
          zIndex: 5,
          // Negative marginTop compensates the safe-area paddingTop
          // in NON-stuck state — without it the natural-flow layout
          // pushes the input pill down by env(safe-area-inset-top)
          // (~59px on Dynamic-Island iPhones), creating a big empty
          // gap between the hero and the search bar. With the
          // negative margin, the wrapper's top edge starts env
          // pixels higher and the internal paddingTop puts the
          // pill at the same visual position as if there were no
          // padding at all. When the bar STICKS, sticky positioning
          // pins it to viewport y=0; the negative margin gets
          // absorbed into the sticky offset and the env paddingTop
          // becomes the morphism cap covering the iOS safe-area
          // zone. Best of both worlds.
          marginTop: "calc(-1 * env(safe-area-inset-top, 0px) + 20px)",
          marginLeft: -20,
          marginRight: -20,
          paddingLeft: 20,
          paddingRight: 20,
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingBottom: 6,
          // Sticky-only morphism. When the bar's at its natural
          // position (page top, not yet stuck), the wrapper is
          // transparent — hero + sky read clean. The moment the
          // user scrolls past the sentinel and the bar sticks, the
          // wrapper picks up `blur(8px)` + a soft glass wash, and
          // because paddingTop = env(safe-area-inset-top) the
          // morphism extends UP through the iOS meta-color zone
          // (under the Dynamic Island), giving a single continuous
          // frosted strip that covers status-bar-overlapping content
          // and the input pill beneath it. `scrolled` flips via the
          // IntersectionObserver on `sentinelRef` above.
          backdropFilter: scrolled ? "blur(8px)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(8px)" : "none",
          background: scrolled ? theme.color.glassFillLite : "transparent",
          boxShadow: scrolled
            ? "0 8px 20px rgba(30,20,8,0.10), 0 1px 0 rgba(30,20,8,0.06)"
            : "none",
          transition: "backdrop-filter 200ms ease, background 200ms ease, box-shadow 200ms ease",
        }}>
        <FadeIn delay={0.06}>
          <GlassPanel
            flat
            tone="input"
            variant="input"
            padding={14}
            style={{
              // Negative margin pulls the input pill UP into the safe-
              // area zone — past the bar's env(safe-area-inset-top)
              // paddingTop. The pill's top edge ends up overlapping
              // the Dynamic Island slightly. The bar's matched cream
              // wash + blur still extends to the viewport top so the
              // visual remains continuous.
              marginTop: -7,
              display: "flex", alignItems: "center", gap: 12,
              // Light glass morphism — modest blur, no saturate.
              // Bg overridden to a more transparent tint so stars
              // / blobs / sky behind the pill stay visible (default
              // input tone is glassFillHeavy ~75% alpha, which
              // blocked them entirely).
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              background: theme.color.glassFillLite,
              border: searchFocused
                ? `1px solid ${theme.color.teal}`
                : undefined,
              boxShadow: searchFocused
                ? `0 0 0 3px ${withAlpha(theme.color.teal, 0.14)}, ${theme.shadow.soft}`
                : undefined,
              transition: "border-color 200ms ease, box-shadow 200ms ease",
            }}
          >
            <SearchGlyph />
            <input
              ref={searchInputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Search or Add to Kitchen"
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                background: "transparent",
                fontFamily: font.sans,
                fontSize: 15,
                color: theme.color.ink,
              }}
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                aria-label="Clear search"
                title="Clear search (Esc)"
                className="mcm-focusable"
                style={{
                  border: "none", background: "transparent", cursor: "pointer",
                  color: theme.color.inkMuted, fontFamily: font.mono, fontSize: 12,
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: 0,
                }}
              >
                {/* Tiny kbd-style cap for the Esc shortcut. Hidden
                    on narrow phones (no physical keyboard) via a
                    media-query class so it doesn't crowd the row;
                    desktop users get the shortcut surfaced inline
                    next to the click affordance. */}
                <span
                  className="mcm-kbd-hint"
                  aria-hidden
                  style={{
                    fontFamily: font.mono, fontSize: 9,
                    fontWeight: 500,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    padding: "1px 5px",
                    borderRadius: 4,
                    border: `1px solid ${theme.color.hairline}`,
                    background: theme.color.glassFillLite,
                    color: theme.color.inkFaint,
                    lineHeight: 1.4,
                  }}
                >
                  esc
                </span>
                CLEAR
              </button>
            )}
            {/* Inline Add affordance — teal "+" pinned to the
                right edge of the search bar. Same row as the
                search input so the two primary entry points
                (find what's in the kitchen, put something new
                in it) read as one toolbar. Hidden when there's
                a live query so CLEAR has the floor. */}
            {onOpenAdd && !query && (
              <button
                onClick={onOpenAdd}
                aria-label="Add an item to the kitchen"
                title="Add an item"
                className="mcm-focusable"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36, height: 36,
                  borderRadius: 999,
                  border: `1px solid ${withAlpha(theme.color.teal, 0.35)}`,
                  background: withAlpha(theme.color.teal, 0.12),
                  color: theme.color.teal,
                  fontSize: 22, lineHeight: 1, fontWeight: 300,
                  cursor: "pointer",
                  flexShrink: 0,
                  transition: "background 160ms ease, border-color 160ms ease, transform 120ms ease",
                }}
              >
                +
              </button>
            )}
          </GlassPanel>

          {/* Location tabs — Fridge / Pantry / Freezer. Mirrors
              classic Kitchen's storageTab axis but rendered as the
              same GlassPill row the MCM demo used for category
              filters, so the visual shape of the row is preserved.
              Hidden while search is active — when a user types,
              the hierarchy gets bypassed and matching items show
              across all locations. NOTE: the location switcher
              itself moved to a FloatingLocationDock at the bottom
              of the screen (see below), so this block is just the
              search; tabs render elsewhere. */}
        </FadeIn>

        {/* Drilled-tile header lives INSIDE the sticky bar so the
            user keeps back/sort/count within reach at any scroll
            depth inside a long tile. When not drilled, this is
            null — sticky bar collapses back to just search + tabs. */}
        {drilledTile && !query && (
          <FadeIn>
            <DrilledTileHeader
              tile={drilledTile}
              location={locationTab}
              count={visible.length}
              warnCount={
                activeHub
                  ? activeHub.warnCount
                  : (warnCountByTile[`${locationTab}:${drilledTile.id}`] || 0)
              }
              hub={activeHub}
              sortBy={sortBy}
              onSortChange={setSortBy}
              // Back pops the hub first when drilled into one, so
              // the user lands on the hub picker; second tap pops
              // back to the tile grid. Mirrors the wizard's
              // step-2 → step-1 stack pop.
              onBack={() => {
                if (drilledHub) { setDrilledHub(null); return; }
                setDrilledTile(null);
              }}
              categoryOptions={categoryOptions}
              categoryFilter={categoryFilter}
              onCategoryChange={setCategoryFilter}
            />
          </FadeIn>
        )}
        </div>

        {/* --- Search summary (which locations did we find hits in?) --
            Drilled-tile header moved INTO the sticky bar above so
            the back button + sort stay reachable during scroll.
            Search summary stays separate (non-sticky) since searches
            don't participate in the tile layout.
            the tapped tile card. Search summary stays up here since
            search doesn't participate in the tile layout. */}
        {/* Search summary only renders when there ARE hits — the
            empty-results case is handled by EmptyState below,
            which already says "Nothing called 'x'" with warmth
            and an ornament. Both rendering at once doubled the
            copy with no extra signal. */}
        {query && visible.length > 0 && (
          <FadeIn>
            <SearchSummary hits={visible} query={query} onClear={() => setQuery("")} />
          </FadeIn>
        )}

        {/* Filter tabs — 3 mutually-exclusive views over the
            location's items. Tapping a tab switches the body to a
            flat list scoped to the active location; tapping the
            same tab again deselects and returns to the tile grid. */}
        {!query && !drilledTile && !(loading && cards.length === 0) && cards.length > 0 && (
          <FadeIn delay={0.1}>
            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 6,
              marginTop: 8,
              flexWrap: "wrap",
            }}>
              {[
                { key: "good",   label: "good",   count: goodCount,   tone: "ok",   color: theme.color.ink },
                { key: "soon",   label: "soon",   count: warnCount,   tone: "warn", color: theme.color.burnt },
                { key: "recent", label: "recent", count: recentCount, tone: "ok",   color: theme.color.ink },
              ].map(({ key, label, count, tone, color }) => {
                const isActive = activeFilter === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveFilter(isActive ? null : key)}
                    aria-pressed={isActive}
                    className="mcm-focusable"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "5px 11px",
                      borderRadius: 999,
                      background: isActive
                        ? color
                        : theme.color.glassFillHeavy,
                      border: `1px solid ${isActive ? color : theme.color.glassBorder}`,
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                      fontFamily: font.mono,
                      fontSize: 12,
                      fontWeight: isActive ? 600 : 500,
                      color: isActive
                        ? theme.color.glassFillHeavy
                        : color,
                      letterSpacing: "0.10em",
                      textTransform: "uppercase",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      ...THEME_TRANSITION,
                    }}
                  >
                    <StatusDot tone={tone} size={6} />
                    {count} {label}
                  </button>
                );
              })}
            </div>
          </FadeIn>
        )}

        {/* --- Body: TILE grid / drilled ITEM grid / SEARCH hits ------- */}
        <AnimatePresence
          mode="wait"
          initial={false}
          onExitComplete={resetScrollAfterExit}
        >
          <motion.div
            key={loading && cards.length === 0 ? "skeleton" : query ? "search" : activeFilter ? `filter-${activeFilter}-${locationTab}` : drilledTile ? (drilledHub ? `drilled-${drilledTile.id}-hub-${drilledHub}` : useHubView ? `drilled-${drilledTile.id}-hubs` : `drilled-${drilledTile.id}`) : `tiles-${locationTab}`}
            // Hardware-accelerated transform string — framer's `y`
            // shorthand runs on rAF/main thread and stutters under
            // load (initial pantry fetch, realtime burst, image
            // decode for the new tile). Full transform string gets
            // GPU compositing and stays smooth.
            initial={{ opacity: 0, transform: "translateY(8px)" }}
            animate={{
              opacity: 1,
              transform: "translateY(0px)",
              // Enter — fast ease-out so drill IN feels instant.
              transition: { duration: 0.22, ease: [0.23, 1, 0.32, 1] },
            }}
            exit={{
              opacity: 0,
              transform: "translateY(-6px)",
              // Exit — longer + ease-in-out for a controlled fade
              // out instead of the front-loaded snap that pure
              // ease-out produced.
              transition: { duration: 0.28, ease: [0.4, 0, 0.2, 1] },
            }}
          >
            {/* SELECT — bulk-cleanup entry. Quiet text pill above any
                item grid (drawer drill, search, filter). Hidden when
                the parent didn't wire bulk handlers, when there's
                nothing selectable, and while select mode is active
                (the bottom bar owns the mode then). */}
            {!selectMode && onDeleteItems && selectableRows.length > 0
              && (query || activeFilter || (drilledTile && (drilledHub || !useHubView))) && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                <button
                  type="button"
                  className="mcm-focusable"
                  onClick={() => setSelectMode(true)}
                  style={{
                    padding: "7px 14px",
                    background: "transparent",
                    border: `1px solid ${theme.color.glassBorder}`,
                    borderRadius: 999,
                    fontFamily: font.mono, fontSize: 12, fontWeight: 600,
                    color: theme.color.ink, opacity: 0.75,
                    letterSpacing: "0.10em",
                    cursor: "pointer", touchAction: "manipulation",
                  }}
                >
                  SELECT
                </button>
              </div>
            )}
            {(() => {
              // Show skeleton while Supabase's initial load is in
              // flight and we have no cached items yet. Once a single
              // row arrives the skeleton steps aside — realtime
              // population feels continuous rather than strobing
              // between ghost and real.
              if (loading && cards.length === 0) return <TileGridSkeleton />;
              if (query)       return <ItemGrid items={visible} onOpenItem={onOpenItem} onOpenUnitPicker={onOpenUnitPicker} onUpdateItem={onUpdateItem} onDeleteItem={onDeleteItem} onRestockItem={onRestockItem} onOpenStack={setOpenStackMembers} brandSuggestions={brandSuggestions} showTileContext {...selectProps} />;
              if (activeFilter) return <ItemGrid items={visible} onOpenItem={onOpenItem} onOpenUnitPicker={onOpenUnitPicker} onUpdateItem={onUpdateItem} onDeleteItem={onDeleteItem} onRestockItem={onRestockItem} onOpenStack={setOpenStackMembers} brandSuggestions={brandSuggestions} showTileContext {...selectProps} />;
              // Tile drilled, no hub picked, and the tile has hub-
              // grouped items worth showing — render the hub picker
              // (chips for each hub + flat KitchenCards for orphans).
              if (drilledTile && !drilledHub && useHubView) {
                return (
                  <HubDrillGrid
                    hubs={tileHubs}
                    orphans={tileOrphans}
                    onPickHub={setDrilledHub}
                    onOpenItem={onOpenItem}
                    onOpenUnitPicker={onOpenUnitPicker}
                    onUpdateItem={onUpdateItem}
                    onDeleteItem={onDeleteItem}
                    onRestockItem={onRestockItem}
                    onOpenStack={setOpenStackMembers}
                    brandSuggestions={brandSuggestions}
                  />
                );
              }
              if (drilledTile) return <ItemGrid items={visible} onOpenItem={onOpenItem} onOpenUnitPicker={onOpenUnitPicker} onUpdateItem={onUpdateItem} onDeleteItem={onDeleteItem} onRestockItem={onRestockItem} onOpenStack={setOpenStackMembers} brandSuggestions={brandSuggestions} {...selectProps} />;
              // Whole-location empty state — when the active
              // location has zero items, skip the wall of dimmed
              // tiles and show a warm dedicated message instead.
              // Keeps the user-experience honest ("your freezer
              // is empty") rather than abandoned-looking.
              const locationTotal = sumLocationTiles(cardsByLocTile[locationTab]);
              if (cards.length > 0 && locationTotal === 0) {
                return <LocationEmptyState location={activeLocation} />;
              }
              return (
                <TileGrid
                  location={activeLocation}
                  cardsByTile={cardsByLocTile[locationTab] || {}}
                  warnCountByTile={warnCountByTile}
                  newCountByTile={newCountByTile}
                  onPickTile={setDrilledTile}
                />
              );
            })()}
          </motion.div>
        </AnimatePresence>
        </LayoutGroup>

        {!loading && (
          (query && visible.length === 0) ||
          (activeFilter && visible.length === 0) ||
          (drilledTile && (drilledHub || !useHubView) && visible.length === 0)
        ) && (
          <EmptyState
            kind={query ? "no-matches" : activeFilter ? "no-matches" : "empty-tile"}
            query={query || (activeFilter ? activeFilter : "")}
            tile={drilledTile}
          />
        )}

        {/* --- Bottom CTA -------------------------------------------
            Two modes here, picked by which props are wired:
              - Showcase / demo mode (onStartCooking provided, no
                onOpenItem): the MCM design-reference Cook CTA
                with the hardcoded "Lemon-butter pasta" preview.
              - Real mode (onOpenItem provided): a triage CTA
                that only appears when there are warn items, and
                tapping it opens the first expiring item in the
                shared ItemCard overlay. Real users care about
                "what needs using" here, not a fake recipe pitch.
            Neither CTA shows while drilled or searching — the
            active flow already has its own focus. */}
        {!drilledTile && !query && onStartCooking && !onOpenItem && (
          <ShowcaseDemoCTA onStartCooking={onStartCooking} />
        )}

        {!drilledTile && !query && onOpenItem && warnCount > 0 && (
          <TriageCTA
            warnCount={warnCount}
            firstExpiring={firstExpiring(cards)}
            onOpenItem={onOpenItem}
          />
        )}

        {/* Positive CTA — shows in real mode when the pantry is
            healthy (no warn items, but stocked). Gives the user a
            forward path into the cook/plan flow instead of an
            empty surface below the tile grid. Only renders when
            `onStartCooking` is wired (App.jsx → setTab("plan"));
            falls silent in Showcase mode (onOpenItem null) or
            when there's nothing on the shelves. */}
        {!drilledTile && !query && onOpenItem && warnCount === 0 && cards.length > 0 && onStartCooking && (
          <FreshCTA
            cards={cards}
            cardsByLocTile={cardsByLocTile}
            onStartCooking={onStartCooking}
          />
        )}
      </div>

      {!hideDock && (
        <BottomDock
          tabs={NAV_TABS}
          activeId="pantry"
          onSelect={(id) => { if (id === "cook") onStartCooking && onStartCooking(); }}
        />
      )}

      {/* Floating location dock — Fridge / Pantry / Freezer pill
          anchored at the bottom of the viewport. Replaces the
          top-of-screen tabs per the "put the pill at the bottom
          floating" direction. Sits ABOVE any app-level nav bar
          (bottom: 96px gives ~80px of space for a 64-72px nav dock
          below it). Hidden while searching since search bypasses
          the location axis anyway. */}
      {/* AnimatePresence so the dock runs its exit animation
          (slide down + fade) when search activates, then runs
          its entrance (slide up + fade) when search is cleared.
          Without this wrapper the dock just pops in/out. */}
      <AnimatePresence>
        {/* Only show the location dock when MCMKitchenScreen is
            embedded in the real app (hideDock=true → the
            internal BottomDock is suppressed, so the location
            dock is the only floating pill at the bottom). In
            Showcase mode the BottomDock is visible as the demo
            app nav; adding FloatingLocationDock there would
            stack two floating pills at the same bottom position. */}
        {!query && hideDock && (
          <FloatingLocationDock
            locations={LOCATIONS}
            active={locationTab}
            onSelect={switchLocation}
            totals={{
              fridge:  sumLocationTiles(cardsByLocTile.fridge),
              pantry:  sumLocationTiles(cardsByLocTile.pantry),
              freezer: sumLocationTiles(cardsByLocTile.freezer),
            }}
          />
        )}
      </AnimatePresence>

      {/* Stack drill-in. Mounted at the screen root so it overlays
          the whole kitchen surface. onOpenMember closes the sheet
          then opens MCMItemCard via the existing onOpenItem path —
          the user lands directly in the editor for the specific
          physical instance they tapped. */}
      {openStackMembers && (
        <StackDetailSheet
          members={openStackMembers}
          onOpenMember={(raw) => {
            setOpenStackMembers(null);
            if (onOpenItem) onOpenItem(raw);
          }}
          onClose={() => setOpenStackMembers(null)}
        />
      )}

      {/* Bulk-select action bar — fixed above the nav while select
          mode is active. ALL + REMOVE = "clear the drawer". */}
      {selectMode && (
        <BulkSelectBar
          count={selectedIds.size}
          total={selectableRows.length}
          onSelectAll={selectAllVisible}
          onClearSelection={clearSelection}
          onCancel={exitSelectMode}
          onRemove={removeSelected}
          onRestock={onRestockItems ? restockSelected : null}
          onPin={pinSelected}
        />
      )}
    </div>
  );
}


// Leaf component that owns the once-a-minute clock tick. Keeps the
// setState scope to this 3-line component instead of cascading a
// re-render through the KitchenScreen and every memoized card.
function ClockKicker({ tone }) {
  const now = useNow();
  return <Kicker tone={tone}>{formatClock(now)}</Kicker>;
}

// AddItem sheet — the rebuild at `additem/AddItemSheet.jsx` is the
// only path. The legacy AddDraftSheet (and its dropdown / picker /
// progress-strip / scan-data-panel siblings) was deleted 2026-05-02
// after Claudes kept silently routing through it via the localStorage
// feature flag.
export { default as MCMAddDraftSheet } from "./additem/AddItemSheet";
